import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import { checkTransactionStatus } from 'components/transactions/transactionTable';
import { alternateCurrencyTags, EMPTY_VALUE, FALL_BACK_TZ } from 'utils/constants';
import { formatNumber } from 'utils/formatNumber';
import { calculateDateOffset } from 'utils/helpers';

const moment = require('moment-timezone');

Font.register({ family: 'Helvetica' });

const styles = StyleSheet.create({
  body: {
    padding: 20,
    fontFamily: 'Helvetica',
  },
  org_info: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  org_name: {
    fontSize: 20,
    fontWeight: 600,
    textTransform: 'capitalize',
    color: '#707070',
    textAlign: 'center',
  },
  logo_bar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: 50,
  },
  logo: {
    display: 'inline-block',
    padding: 8,
    border: 1,
    borderColor: '#D8D8E3',
    borderRadius: 5,
  },
  passport: {
    borderRadius: 5,
    width: 120,
  },
  status: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#c7ffd5',
    borderRadius: 5,
    minHeight: 30,
  },
  status_text: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#fff',
  },
  trans_bar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 40,
  },
  trans_text: {
    fontSize: 15,
    fontWeight: 600,
    textTransform: 'capitalize',
    color: '#707070',
  },
  trans_date: {
    fontSize: 11,
    textTransform: 'capitalize',
    color: '#707070',
  },
  rrr: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: '#f5f5f7',
    borderRadius: 5,
    marginBottom: 10,
    minHeight: 30,
  },
  applicant_org_logo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10
  },
  org_logo: {
    borderRadius: 5,
    width: 60,
  },
  rrr_text: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#a6a6bd',
  },
  platform: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginBottom: 5,
  },
  platform_text: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#a6a6bd',
    marginRight: 5,
  },
  platform_text_main: {
    fontSize: 15,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#707070',
    marginTop: 10,
    marginBottom: -2,
  },
  transaction_info: {
    padding: '20px 0px',
    flexDirection: 'row',
    borderStyle: 'solid',
    borderTopWidth: 1,
    borderColor: '#dddde6',
  },
  form_field_title: {
    display: 'block',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#707070',
    fontSize: 12,
  },
  form_field_subtitle: {
    display: 'block',
    color: '#919191',
    fontSize: 12,
    margin: '3px 0px 0px 0',
  },
  payment_items_container: {
    marginTop: 50,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#dddde6',
    borderRadius: 5,
    overflow: 'hidden',
  },
  payment_items_header: {
    padding: '10px 10px',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f7',
    borderRadius: 5,
  },
  header_text: {
    display: 'block',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#707070',
    fontSize: 11,
  },
  payment_item: {
    padding: '10px 10px',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderStyle: 'solid',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#f1f1f6',
  },
  item_text: {
    display: 'block',
    textTransform: 'uppercase',
    color: '#707070',
    fontSize: 11,
  },
});

const status = {
  successful: 'success',
  failed: 'failed',
  pending: 'pending',
};
const statusText = {
  success: 'successful',
  failed: 'failed',
  pending: 'pending',
};

const statusColors = {
  [status.successful]: '#3EB556',
  [status.failed]: '#EF341C',
  [status.pending]: '#D4A334',
};

const TransactionPdf = ({ data: { data, applicant, org, timezone } }) => {
  const statusTag = (status) => (
    <View style={[styles.status, { backgroundColor: statusColors[status] }]}>
      <Text style={styles.status_text}>{statusText[status]} Transaction</Text>
    </View>
  );

  const checkPaymentPlatform = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'remita':
        return <Text style={styles.platform_text_main}>Remita</Text>;
      case 'paystack':
        return <Text style={styles.platform_text_main}>Paystack</Text>;
      case 'interswitch':
        return <Text style={styles.platform_text_main}>Interswitch</Text>;
      default:
        return <Text style={styles.platform_text_main}>Stripe</Text>;
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <View style={styles.applicant_org_logo}>
          <Image style={styles.org_logo} src={org?.logoUrl} />
        </View>
        <View style={styles.org_info}>
          <Text style={styles.org_name}>{org?.name}</Text>
        </View>
        <View style={styles.trans_bar}>
          <View>
            <Text style={styles.trans_text}>transaction details</Text>
            <Text style={styles.trans_date}>
              {moment(calculateDateOffset(data?.createdAt, timezone))
                .tz(timezone || FALL_BACK_TZ)
                .format('MMMM Do YYYY, h:mm:ss a')}
            </Text>
          </View>
          <View>
            <View style={styles.platform}>
              <Text style={styles.platform_text}>Platform</Text>
              {checkPaymentPlatform(data?.platform.toLowerCase())}
            </View>
            <View style={styles.rrr}>
              <Text style={styles.rrr_text}>
                RRR -{' '}
                <View>
                  <Text style={{ color: '#737373' }}>
                    {data?.rrr || EMPTY_VALUE}
                  </Text>
                </View>
              </Text>
            </View>
            {statusTag(checkTransactionStatus(data?.status.toLowerCase()))}
          </View>
        </View>

        <View style={styles.transaction_info}>
          <View style={{ width: '40%' }}>
            <Text style={styles.form_field_title}>Name</Text>
            <Text style={styles.form_field_subtitle}>
              {data?.name || EMPTY_VALUE}
            </Text>
          </View>
          <View style={{ width: '60%' }}>
            <Text style={styles.form_field_title}>Payers Email</Text>
            <Text style={styles.form_field_subtitle}>
              {data?.email || EMPTY_VALUE}
            </Text>
          </View>
        </View>

        <View style={styles.transaction_info}>
          <View style={{ width: '40%' }}>
            <Text style={styles.form_field_title}>Application</Text>
            <Text style={styles.form_field_subtitle}>
              {applicant?.applicationTitle}
            </Text>
          </View>
          <View style={{ width: '60%' }}>
            <Text style={styles.form_field_title}>Course</Text>
            <Text style={styles.form_field_subtitle}>
              {applicant?.course?.code} - {applicant?.course?.title}
            </Text>
          </View>
        </View>

        <View style={styles.transaction_info}>
          <View style={{ width: '40%' }}>
            <Text style={styles.form_field_title}>Transaction Reference</Text>
            <Text style={styles.form_field_subtitle}>
              {data?.reference || EMPTY_VALUE}
            </Text>
          </View>
          <View style={{ width: '60%' }}>
            <Text style={styles.form_field_title}>RRR</Text>
            <Text style={styles.form_field_subtitle}>
              {data?.rrr || EMPTY_VALUE}
            </Text>
          </View>
        </View>

        <View style={styles.payment_items_container}>
          <View style={styles.payment_items_header}>
            <Text style={styles.header_text}>Description</Text>
            <Text style={styles.header_text}>Amount</Text>
          </View>
          {data?.items
            .filter((fee) => fee.amount !== 0)
            .map((fee, i) => (
              <View key={i} style={styles.payment_item}>
                <Text style={styles.item_text}>{fee.name}</Text>
                <Text style={styles.item_text}>
                  {alternateCurrencyTags[fee?.currency || 'NGN']}
                  {formatNumber(fee.amount)}
                </Text>
              </View>
            ))}
          {data?.gatewayCharge && data?.gatewayCharge !== 0 && (
            <View style={styles.payment_item}>
              <Text style={styles.item_text}>Payment Gateway Fee</Text>
              <Text style={styles.item_text}>
                {alternateCurrencyTags[data?.currency || 'NGN']}
                {formatNumber(data?.gatewayCharge)}
              </Text>
            </View>
          )}
          <View style={styles.payment_items_header}>
            <Text style={styles.header_text}>Total</Text>
            <Text style={styles.header_text}>
              {alternateCurrencyTags[data?.currency || 'NGN']}
              {formatNumber(data?.amount + data?.gatewayCharge || 0)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default TransactionPdf;

TransactionPdf.propTypes = {
  data: PropTypes.object,
  logoUrl: PropTypes.string,
};
